// client/src/features/buildings/enums.ts — the „Clădiri" module's vocabularies · D-3355, D-3361.
//
// Every enum the module's nine tables and read models carry is declared server-side in ONE lane
// (lane 1's `SupportingEnums.cs`) and labelled in ONE place (`enums.*` in romanian.json), so the
// parallel lanes 2–4 add no member here either. A vocabulary the contract already carries is
// declared `as const satisfies readonly T[]` against the GENERATED union, so a member the server
// drops is a compile error in the PR that regenerated the client. Since the lanes merged
// (D-3397) every vocabulary a route carries is in that form; `BuildingResidentStatus` is the one
// no route answers, so it is still written out from the C# enum in the same order, and whoever
// first puts it on the wire swaps its line when the generated type appears.

import type {
  BuildingReadingCellState,
  BuildingReadingSource,
  BuildingReadingWindowState,
  BuildingResidentKind,
  BuildingResidentOrigin,
  BuildingTicketAuthorKind,
  BuildingTicketAuthorSide,
  BuildingTicketCategory,
  BuildingTicketScope,
  BuildingTicketStatus,
} from '@/client';

/** `BuildingResidentKind` — owner of the apartment, or its tenant (BR-BLD-05). */
export const BUILDING_RESIDENT_KINDS = [
  'OWNER',
  'TENANT',
] as const satisfies readonly BuildingResidentKind[];

/**
 * `BuildingResidentOrigin` — an invitation or the administrator's own mark, or a current contract on
 * the linked property (D-3651). A `LEASE` residency is never ended by „Încheie".
 */
export const BUILDING_RESIDENT_ORIGINS = [
  'MANUAL',
  'LEASE',
] as const satisfies readonly BuildingResidentOrigin[];

/** `BuildingResidentStatus` — a residency is ended, never deleted. */
export const BUILDING_RESIDENT_STATUSES = ['ACTIVE', 'ENDED'] as const;

/** `BuildingReadingSource` — a resident inside the window, or the administrator (BR-BLD-09). */
export const BUILDING_READING_SOURCES = [
  'RESIDENT',
  'ADMIN',
] as const satisfies readonly BuildingReadingSource[];

/** `BuildingReadingCellState` — one cell of the readings grid for one period. */
export const BUILDING_READING_CELL_STATES = [
  'MISSING',
  'SUBMITTED',
  'ENTERED',
  'PENDING_PHOTO',
] as const satisfies readonly BuildingReadingCellState[];

/** `BuildingReadingWindowState` — where today falls against the clădire's window. */
export const BUILDING_READING_WINDOW_STATES = [
  'NOT_YET',
  'OPEN',
  'CLOSED',
] as const satisfies readonly BuildingReadingWindowState[];

/** `BuildingTicketScope` — the whole clădire, or one apartment (BR-BLD-10). */
export const BUILDING_TICKET_SCOPES = [
  'COMMON',
  'APARTMENT',
] as const satisfies readonly BuildingTicketScope[];

/** `BuildingTicketCategory` — the trade, in the C# enum's order. */
export const BUILDING_TICKET_CATEGORIES = [
  'ELEVATOR',
  'STAIRWELL',
  'LIGHTING',
  'WATER_SEWER',
  'HEATING',
  'ROOF_FACADE',
  'CLEANING',
  'NOISE',
  'OTHER',
] as const satisfies readonly BuildingTicketCategory[];

/** `BuildingTicketStatus` — the board's columns, in order. The transition table is lane 4's. */
export const BUILDING_TICKET_STATUSES = [
  'OPEN',
  'IN_PROGRESS',
  'DONE',
  'CANCELLED',
] as const satisfies readonly BuildingTicketStatus[];

/** `BuildingTicketAuthorKind` — who opened a ticket, as a resident reads it (D-3651). */
export const BUILDING_TICKET_AUTHOR_KINDS = [
  'OWNER',
  'TENANT',
  'ADMINISTRATOR',
] as const satisfies readonly BuildingTicketAuthorKind[];

/** `BuildingTicketAuthorSide` — who wrote a ticket or a comment: the administrator or a resident. */
export const BUILDING_TICKET_AUTHOR_SIDES = [
  'ADMIN',
  'RESIDENT',
] as const satisfies readonly BuildingTicketAuthorSide[];

/**
 * The tabs of SCR-BLD-002, in the bar's order (04 §8.21). Apartamente and Setări are lane 1's;
 * Locatari is lane 2's, Contoare and Citiri lane 3's, Tichete lane 4's — each a component in its
 * lane's own folder, mounted by the profile.
 */
export const BUILDING_TABS = [
  'apartments',
  'residents',
  'meters',
  'readings',
  'tickets',
  'settings',
] as const;

export type BuildingTab = (typeof BUILDING_TABS)[number];
